import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { RentalReportFilterModel } from 'src/app/models/rental/rental-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InventoryPeriodService } from 'src/app/services/inventory/inventory-period.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalReportService } from 'src/app/services/rental/rental-report.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-historical-movement-workshop',
  templateUrl: './historical-movement-workshop.component.html',
  styleUrls: ['./historical-movement-workshop.component.scss'],
  standalone: false
})
export class HistoricalMovementWorkshopComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  formRentalReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listBranch: any[] = [];
  listCostCentersWorkshop: any[] = [];
  listWorkshop: CustomListDropdown[] = [{ label: 'Todos', value: 0 }, { label: 'Taller 2', value: 2 }, { label: 'Taller 3', value: 3 }];
  nameWindow: string = 'Informe Movimiento Histórico Taller';
  projectName = ProjectName.projectName;
  workshopId: number = 0;

  constructor(private articleService: ArticleService,
    private branchService: BranchService,
    private costCenterService: CostCenterService,
    private formBuilder: FormBuilder,
    private headerService: HeaderService,
    private inventoryPeriodService: InventoryPeriodService,
    private rentalReportService: RentalReportService,
    private title: Title,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm();
  }

  async ngOnInit() {
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.getFirstBranchByUserAuthenticatedAsync();
    await this.getAllLists();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeArticle(articleId: number | null) {
    if (articleId === 0 || articleId === null || articleId === undefined) {
      this.formRentalReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeWorkshop(workshop: number) {
    const workshopId = this.listCostCentersWorkshop.find(x => x.code === workshop)?.costCenterId;
    if (workshopId) {
      this.workshopId = workshopId;
    } else {
      this.workshopId = 0;
    }
    this.changeFilter();
  }

  clear() {
    this.workshopId = 0;
    this.changeFilter();
    this.validateForm();
    this.getFirstBranchByUserAuthenticatedAsync();
  }

  async exportExcel() {
    if (this.changeFilters) {
      const modelRentalReportFilter = this.formRentalReportFilter.value as RentalReportFilterModel;

      const dateInitial = this.formRentalReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      modelRentalReportFilter.year = parseInt(year);
      modelRentalReportFilter.period = parseInt(period);

      const dateFinal = this.formRentalReportFilter.get("dateFinal")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      modelRentalReportFilter.yearFinal = parseInt(yearFinal);
      modelRentalReportFilter.periodFinal = parseInt(periodFinal);

      modelRentalReportFilter.costCenterId = this.workshopId;
      try {
        const response = await firstValueFrom(this.rentalReportService.exportRentalReportHistoricalMovementWorkshop(modelRentalReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe Movimiento Histórico Taller.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists() {
    try {
      const [branches, costCenterWorkshops, articles] = await Promise.all([
        firstValueFrom(this.branchService.getAllBranchesForSelectAsync()),
        firstValueFrom(this.costCenterService.getAllCostCentersWorkshop()),
        firstValueFrom(this.articleService.getAllArticlesForSelectAsync()),
      ]);
      this.listBranch = branches;
      this.listCostCentersWorkshop = costCenterWorkshops;
      this.listArticle = articles;
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticatedAsync() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formRentalReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.getLastInventoryPeriod();
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getLastInventoryPeriod() {
    try {
      const inventoryPeriod = await firstValueFrom(this.inventoryPeriodService.getLastInventoryPeriod());
      this.formRentalReportFilter.controls["dateInitial"].patchValue(moment(inventoryPeriod.initialDate).startOf('month').toDate());
      this.formRentalReportFilter.controls["dateFinal"].patchValue(moment(inventoryPeriod.finalDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de inventario:", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    Object.keys(this.formRentalReportFilter.controls).forEach(key => {
      const control = this.formRentalReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formRentalReportFilter.valid) {
      const modelRentalReportFilter = this.formRentalReportFilter.value as RentalReportFilterModel;

      const dateInitial = this.formRentalReportFilter.get("dateInitial")?.value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      modelRentalReportFilter.year = parseInt(year);
      modelRentalReportFilter.period = parseInt(period);

      const dateFinal = this.formRentalReportFilter.get("dateFinal")?.value;
      const finalDate = `${dateFinal.getFullYear()}-${(dateFinal.getMonth() + 1).toString().padStart(2, '0')}`;
      const periodFinal = finalDate.slice(5);
      const yearFinal = finalDate.slice(0, -3);

      modelRentalReportFilter.yearFinal = parseInt(yearFinal);
      modelRentalReportFilter.periodFinal = parseInt(periodFinal);

      modelRentalReportFilter.costCenterId = this.workshopId;

      if (dateFinal < dateInitial) {
        this.toastHandlerService.showToast('warning', 'El período final no puede ser menor al período inicial.');
        return;
      }

      try {
        this.dataReport = await firstValueFrom(this.rentalReportService.rentalReportHistoricalMovementWorkshop(modelRentalReportFilter));
        if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
          this.changeFilters = true;
        } else {
          this.changeFilters = false;
          this.toastHandlerService.showToast('info', 'No se encontraron datos.');
        }
      } catch (error) {
        console.error("Error al consultar el informe:", error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Período Inicial es obligatorio.'
      },
      dateFinal: {
        required: 'El campo Período Final es obligatorio.'
      },
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      costCenterId: {
        required: 'El campo Taller es obligatorio.',
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formRentalReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formRentalReportFilter = this.formBuilder.group({
      dateInitial: ['', [Validators.required]],
      dateFinal: ['', [Validators.required]],
      branchId: [0, [Validators.required, Validators.min(1)]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]],
      costCenterId: [0, [Validators.required]],
      articleName: [""]
    });
  }

}
